import React, { useEffect, useMemo, useState, useRef, Fragment } from "react"
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { useSelector } from 'react-redux'
import { Modal, Dropdown, DropdownButton } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from "react-datepicker"

import NotInventored from "pages/main/inventory/NotInventored"
import ReactSelect from 'pages/parts/ReactSelect'
import useVirtualRows from 'pages/parts/useVirtualRows'
import usePreventExit from "pages/parts/usePreventExit"
import { httpOk, GET, POST, PUT } from 'helpers/api'
import { handleDecimalKeyDown } from 'helpers/decimalKey'
import { formatDate, formatMoney, formatMoneyInput, returnMinDate, notify } from 'helpers/helpers'

// Высота строки таблицы — должна совпадать с .doc-table tbody td в style.css
const ROW_HEIGHT = 33
// Сколько позиций справочника показываем в выпадающем списке за раз
const OPTIONS_LIMIT = 50

function Main() {
	const { t } = useTranslation();
	const { id } = useParams();
	const navigate = useNavigate();

	const [isDirty, setIsDirty] = useState(false);
	usePreventExit(isDirty);

	const tbodyRef = useRef()
	const scrollRef = useRef()

	const reduxAccount = useSelector(state => state.account)

	const [data, setData] = useState({
		'pos_id': reduxAccount.pos_id,
		'wms_id': reduxAccount.wms_id,
		'elements': [],
		'products': [],
		'created_at': formatDate(new Date(), 'yyyy-MM-dd'),
	})
	const [activeTab, setActiveTab] = useState(1)
	const [wms, setWms] = useState([])
	const [products, setProducts] = useState([])
	const [productsDuplicate, setProductsDuplicate] = useState([])
	const [elements, setElements] = useState([])
	const [elementsDuplicate, setElementsDuplicate] = useState([])
	const [addQuantity, setAddQuantity] = useState(1)
	const [modalStep, setModalStep] = useState(false)
	const [modalConfirm, setModalConfirm] = useState(false)
	const [modalDuplicates, setModalDuplicates] = useState(false)
	const [duplicateSummary, setDuplicateSummary] = useState([])
	const [modalNotInventored, setModalNotInventored] = useState(false)

	// Шаг 1 — таблица заготовок, шаг 2 — таблица элементов.
	// В DOM держим только видимые строки: при 2000 позициях полная отрисовка
	// подвешивает браузер на каждом нажатии клавиши.
	const virtualProducts = useVirtualRows({
		count: data.products.length,
		rowHeight: ROW_HEIGHT,
	})

	// Строка с группами занимает столько строк, сколько в ней групп
	const elementRowHeights = data.elements.map(
		(item) => ROW_HEIGHT * Math.max(1, item?.groups?.length || 1)
	)

	const virtualElements = useVirtualRows({
		count: data.elements.length,
		rowHeight: ROW_HEIGHT,
		rowHeights: elementRowHeights,
	})

	// В выпадающем списке рисуем ограниченную выдачу: 2000 <tr> в меню
	// подвешивают браузер на самом открытии
	const productOptions = useMemo(() => products.slice(0, OPTIONS_LIMIT), [products])
	const elementOptions = useMemo(() => elements.slice(0, OPTIONS_LIMIT), [elements])

	function getElementActualQty(item) {
		if (item?.groups?.length > 0) {
			return item.groups.reduce((sum, group) => sum + Number(group.actual_balance || 0), 0)
		}
		return Number(item?.actual_balance || 0)
	}

	function getDuplicateElementsSummary(elements = []) {
		const map = {}
		elements.forEach((item, index) => {
			if (!item?.element_id) return
			const key = item.element_id
			const qty = getElementActualQty(item)
			if (!map[key]) {
				map[key] = {
					element_id: key,
					name: item.name,
					unit_name: item.unit_name,
					entries: [],
					total: 0,
				}
			}
			map[key].entries.push({
				row: index + 1,
				qty,
			})
			map[key].total += qty
		})
		return Object.values(map).filter(item => item.entries.length > 1)
	}

	/** Перед сохранением схлопываем одинаковые element_id в одну строку (сумма факта) */
	function mergeElementsForSave(elements = []) {
		const merged = []
		const indexById = {}

		elements.forEach((item) => {
			if (!item?.element_id) {
				merged.push({ ...item })
				return
			}
			const key = item.element_id
			if (indexById[key] === undefined) {
				indexById[key] = merged.length
				const copy = { ...item }
				delete copy.groups
				copy.actual_balance = getElementActualQty(item)
				merged.push(copy)
				return
			}
			const target = merged[indexById[key]]
			target.actual_balance = Number(target.actual_balance || 0) + getElementActualQty(item)
		})

		return merged
	}

	function requestComplete() {
		const duplicates = getDuplicateElementsSummary(data.elements)
		if (duplicates.length > 0) {
			setDuplicateSummary(duplicates)
			setModalDuplicates(true)
			return
		}
		setModalConfirm(true)
	}

	async function inventoryCompleted(completed = false) {
		var sendData = { ...data }
		sendData.elements = mergeElementsForSave(sendData.elements)

		for (let i = 0; i < sendData.elements.length; i++) {
			sendData.elements[i]['difference_amount'] =
				(sendData.elements[i]['actual_balance'] - sendData.elements[i]['balance']) * sendData.elements[i]['price']
			sendData.elements[i]['divergence'] = sendData.elements[i]['actual_balance'] - sendData.elements[i]['balance']
		}

		if (sendData.error) {
			notify(t('check_filled_inputs'))
			return
		}

		setModalDuplicates(false)
		setModalConfirm(false)

		var response;
		if (id) {
			if (completed) {
				response = await PUT('/inventory-completed', sendData, { loader: true })
				if (httpOk(response)) {
					flushSync(() => setIsDirty(false))
					navigate('/inventory-wms')
				}
			} else {
				response = await PUT('/inventory', sendData, { loader: true })
				if (httpOk(response)) {
					flushSync(() => setIsDirty(false))
					navigate('/inventory-wms')
				}
			}
		} else {
			if (completed) {
				response = await PUT('/inventory-completed', sendData, { loader: true })
				if (httpOk(response)) {
					flushSync(() => setIsDirty(false))
					navigate('/inventory-wms')
				}
			} else {
				response = await POST('/inventory', sendData, { loader: true })
				if (httpOk(response)) {
					flushSync(() => setIsDirty(false))
					navigate('/inventory-wms')
				}
			}
		}
	}

	// STEP 1
	// REACTSELECT LOGIC
	function handleSelectSearchProduct(value) {
		if (!value) {
			setProducts([...productsDuplicate])
			return
		}

		var productsDuplicateCopy = [...productsDuplicate]
		var filteredItems = productsDuplicateCopy.filter(item => item.name.toLowerCase().includes(value.toLowerCase()))
		if (filteredItems[0]) {
			filteredItems[0]['selected'] = true
		}
		setProducts(filteredItems)
	}

	function handleSelectProduct(option, index) {
		var dataCopy = { ...data }

		let exist = dataCopy.products.findIndex(item => item.product_id === option.product_id);
		if (exist !== -1) {
			notify(`Дубликат: строка ${exist + 1}`)
			return
		} else {
			dataCopy.products[index] = option
		}

		setIsDirty(true)
		setData(dataCopy)
	}

	function closeReactSelectProduct() {
		setTimeout(() => {
			var elementsCopy = [...elements]
			for (let i = 0; i < elementsCopy.length; i++) {
				elementsCopy[i]['selected'] = false
			}
			if (elementsCopy[0] && elementsCopy[0]['selected'])
				elementsCopy[0]['selected'] = true

			setElements(elementsCopy)
		}, 100);
	}

	function handleTableKeyDownProduct(event) {
		event.stopPropagation();
		// Стрелками ходим по тому, что реально показано в меню
		var currentIndex = productOptions.findIndex(el => el.selected);
		if (currentIndex >= 0) {
			productOptions[currentIndex].selected = false
			switch (event.key) {
				case "ArrowUp":
					currentIndex = currentIndex === 0 ? productOptions.length - 1 : currentIndex - 1
					break;
				case "ArrowDown":
					currentIndex = currentIndex === productOptions.length - 1 ? 0 : currentIndex + 1
					break;
				default: return;
			}

			productOptions[currentIndex].selected = true
			if (scrollRef.current) {
				scrollRef.current.scrollTop = (currentIndex * 29.6) - 29.6
			}

			setProducts([...products])
		}
	}
	// REACTSELECT LOGIC

	function addToList(length = 1, type = 'product') {
		var dataCopy = JSON.parse(JSON.stringify(data))
		var firstNewIndex = type === 'product' ? dataCopy.products.length : dataCopy.elements.length

		if (type === 'product') {
			for (let i = 0; i < length; i++) {
				dataCopy.products.push({})
			}
		}

		if (type === 'element') {
			for (let i = 0; i < length; i++) {
				dataCopy.elements.push({})
			}
		}

		setIsDirty(true)
		setData(dataCopy)

		// Новая строка может быть за пределами окна виртуализации —
		// сначала подводим её к экрану, потом ставим курсор
		setTimeout(() => {
			const virtual = type === 'product' ? virtualProducts : virtualElements
			virtual.scrollToIndex(firstNewIndex, `input-select-${firstNewIndex + 1}`)
		}, 60);
	}

	function deleteProduct(index) {
		var dataCopy = { ...data }
		dataCopy.products.splice(index, 1)
		setIsDirty(true)
		setData(dataCopy)
	}

	function deleteProducts() {
		var dataCopy = { ...data }
		dataCopy.products = []
		setIsDirty(true)
		setData(dataCopy)
	}

	function handleProductChange(e, index) {
		var dataCopy = { ...data }
		if (Number(e.target.value) > 999999999) {
			notify(t('exceeds_maximum'))
			dataCopy.products[index][e.target.name] = ""
			setData(dataCopy)
			return;
		}

		let value = e.target.value;
		value = value.replace(/,/g, '.')
		dataCopy.products[index][e.target.name] = value.replace(/[^\d.]/g, '')
		setIsDirty(true)
		setData(dataCopy)
	}

	function toggleModalStep(bool) {
		setModalStep(bool)
	}

	function fillProducts(in_product) {
		var dataCopy = { ...data }

		if (in_product === 1) {
			for (let i = 0; i < productsDuplicate.length; i++) {
				if (productsDuplicate[i]['in_product'] === 1) {
					if (!dataCopy.products.some(item => item.product_id === productsDuplicate[i]['product_id'])) {
						dataCopy.products.push(productsDuplicate[i])
					}
				}
			}
		}

		if (in_product === 2) {
			for (let i = 0; i < productsDuplicate.length; i++) {
				if (productsDuplicate[i]['in_product'] === 2) {
					if (!dataCopy.products.some(item => item.product_id === productsDuplicate[i]['product_id'])) {
						dataCopy.products.push(productsDuplicate[i])
					}
				}
			}
		}

		setIsDirty(true)
		setData(dataCopy)
	}

	function handleChangeWMS(option) {
		setData({ ...data, 'wms_id': option.id, 'wms_name': option.name })
		getProducts()
		getElements(option.id)
	}

	async function confirmStep() {
		var dataCopy = { ...data }

		dataCopy.error = false
		dataCopy.products = dataCopy.products.filter(item => item.product_id !== null && item.product_id !== '');
		for (let i = 0; i < dataCopy.products.length; i++) {
			if (dataCopy.products[i]['actual_balance'] < 0 || dataCopy.products[i]['actual_balance'] === "") {
				notify(t('check_filled_inputs'))
				return
			}
		}

		var response = await POST('/inventory-calculate', {
			'products': dataCopy.products
		}, { loader: true })
		
		if (httpOk(response)) {
			dataCopy.elements = response.data
			for (let i = 0; i < dataCopy.elements.length; i++) {
				for (let j = 0; j < elementsDuplicate.length; j++) {
					if (dataCopy.elements[i]['element_id'] === elementsDuplicate[j]['element_id']) {
						dataCopy.elements[i]['balance_id'] = elementsDuplicate[j]['balance_id']
						dataCopy.elements[i]['name'] = elementsDuplicate[j]['name']
						dataCopy.elements[i]['unit_name'] = elementsDuplicate[j]['unit_name']
						dataCopy.elements[i]['price'] = elementsDuplicate[j]['price']
						dataCopy.elements[i]['balance'] = elementsDuplicate[j]['balance']
						dataCopy.elements[i]['divergence'] = ""
						dataCopy.elements[i]['difference_amount'] = ""
					}
				}
			}

			toggleModalStep(false)
			setActiveTab(2)
			setIsDirty(true)
			calculate(dataCopy)
		}
	}
	// STEP 1

	// STEP 2
	// REACTSELECT LOGIC
	function handleSelectSearch(value) {
		if (!value) {
			setElements([...elementsDuplicate])
			return
		}
		var elementsDuplicateCopy = [...elementsDuplicate]

		var filteredItems = elementsDuplicateCopy.filter(item => item.name.toLowerCase().includes(value.toLowerCase()))
		if (filteredItems[0]) {
			filteredItems[0]['selected'] = true
		}
		setElements(filteredItems)
	}

	function handleSelectElement(option, index) {
		var dataCopy = { ...data }

		dataCopy.elements[index]['balance_id'] = option.balance_id
		dataCopy.elements[index]['element_id'] = option.element_id
		dataCopy.elements[index]['name'] = option.name
		dataCopy.elements[index]['product_type_name'] = option.product_type_name
		dataCopy.elements[index]['unit_name'] = option.unit_name
		dataCopy.elements[index]['price'] = option.price
		dataCopy.elements[index]['balance'] = option.balance
		if (dataCopy.elements[index]['actual_balance'] === undefined || dataCopy.elements[index]['actual_balance'] === null) {
			dataCopy.elements[index]['actual_balance'] = ''
		}
		dataCopy.elements[index]['divergence'] = ''
		dataCopy.elements[index]['difference_amount'] = ''

		setIsDirty(true)
		setData(dataCopy)
	}

	function closeReactSelect() {
		setTimeout(() => {
			var elementsCopy = [...elements]
			for (let i = 0; i < elementsCopy.length; i++) {
				elementsCopy[i]['selected'] = false
			}
			if (elementsCopy[0] && elementsCopy[0]['selected'])
				elementsCopy[0]['selected'] = true
			setElements(elementsCopy)
		}, 100);
	}

	function handleTableKeyDown(event) {
		event.stopPropagation();
		// Стрелками ходим по тому, что реально показано в меню
		var currentIndex = elementOptions.findIndex(el => el.selected);
		if (currentIndex >= 0) {
			elementOptions[currentIndex].selected = false
			switch (event.key) {
				case "ArrowUp":
					currentIndex = currentIndex === 0 ? elementOptions.length - 1 : currentIndex - 1
					break;
				case "ArrowDown":
					currentIndex = currentIndex === elementOptions.length - 1 ? 0 : currentIndex + 1
					break;
				default: return;
			}

			elementOptions[currentIndex].selected = true
			if (scrollRef.current) {
				scrollRef.current.scrollTop = (currentIndex * 29.6) - 29.6
			}

			setElements([...elements])
		}
	}
	// REACTSELECT LOGIC

	function handleElementChange(e, index) {
		var dataCopy = { ...data }
		if (Number(e.target.value) > 999999999) {
			notify(t('exceeds_maximum'))
			dataCopy.elements[index][e.target.name] = ""
			setData(dataCopy)
			return;
		}

		let value = e.target.value;
		value = value.replace(/,/g, '.')
		dataCopy.elements[index][e.target.name] = value.replace(/[^\d.]/g, '')
		setIsDirty(true)
		calculate(dataCopy)
	}

	function handleGroupElementChange(e, index, index2) {
		var dataCopy = { ...data }

		let value = e.target.value;
		value = value.replace(/,/g, '.')
		dataCopy.elements[index]['groups'][index2][e.target.name] = value.replace(/[^\d.]/g, '')

		setIsDirty(true)
		calculate(dataCopy)
	}

	function addGroupToList(item, index) {
		var dataCopy = JSON.parse(JSON.stringify(data))
		var element = {
			"element_id": item.element_id,
			"actual_balance": ""
		}

		if (dataCopy.elements[index].groups?.length > 0) {
			dataCopy.elements[index]['groups'].push(element)
		} else {
			dataCopy.elements[index]['groups'] = [{
				...element,
				'actual_balance': item.actual_balance,
				'product_id': item.product_id,
			}]
			dataCopy.elements[index]['groups'].push(element)
		}

		setIsDirty(true)
		setData(dataCopy)
	}

	function deleteGroupProduct(index, index2) {
		var dataCopy = { ...data }

		dataCopy.elements[index]['groups'].splice(index2, 1)
		if (dataCopy.elements[index]['groups'].length === 1) {
			delete dataCopy.elements[index]['groups'];
		}

		setIsDirty(true)
		setData(dataCopy)
	}

	function deleteElement(index) {
		var dataCopy = { ...data }
		dataCopy.elements.splice(index, 1)
		setIsDirty(true)
		setData(dataCopy)
	}

	function calculateGroup(item) {
		var fact = 0

		for (let i = 0; i < item.groups.length; i++) {
			fact += Number(item.groups[i]['actual_balance'])
		}

		return formatMoney(fact, 2)
	}

	function calculate(dataCopy) {
		dataCopy.balance = 0
		dataCopy.difference_quantity = 0
		dataCopy.difference_amount = 0

		for (let i = 0; i < dataCopy.elements.length; i++) {
			if (dataCopy.elements[i]['groups'] && dataCopy.elements[i]['groups'].length > 1) {
				var temporaryActualBalance = 0;
				for (let j = 0; j < dataCopy.elements[i]['groups'].length; j++) {
					temporaryActualBalance += Number(dataCopy.elements[i]['groups'][j]['actual_balance']);
				}

				dataCopy.elements[i]['divergence'] =
					temporaryActualBalance - dataCopy.elements[i]['balance']
				dataCopy.elements[i]['difference_amount'] =
					(temporaryActualBalance - dataCopy.elements[i]['balance']) * dataCopy.elements[i]['price']
			} else {
				dataCopy.elements[i]['divergence'] =
					dataCopy.elements[i]['actual_balance'] - dataCopy.elements[i]['balance']
				dataCopy.elements[i]['difference_amount'] =
					(dataCopy.elements[i]['actual_balance'] - dataCopy.elements[i]['balance']) * dataCopy.elements[i]['price']
			}

			dataCopy.difference_quantity += Number(dataCopy.elements[i]['divergence']) || 0
			dataCopy.difference_amount += Number(dataCopy.elements[i]['difference_amount']) || 0
		}

		setData(dataCopy)
	}

	async function notInventoredList() {
		setModalNotInventored(true)
	}
	// STEP 2

	async function getWms() {
		const response = await GET(`/helper/wms?pos_id=${reduxAccount.pos_id}`)
		if (httpOk(response)) setWms(response.data)
	}

	async function getProducts(wms_id = reduxAccount.wms_id) {
		var sendData = {
			"pos_id": reduxAccount.pos_id,
			"wms_id": wms_id,
		}

		const response = await GET(`/inventory-products`, sendData)
		if (httpOk(response)) {
			setProducts(response.data)
			setProductsDuplicate(response.data)
		}
	}

	async function getElements(wms_id = reduxAccount.wms_id) {
		var sendData = {
			"pos_id": reduxAccount.pos_id,
			"wms_id": wms_id,
			"created_at": data.created_at,
		}
		const response = await GET(`/inventory-elements`, sendData)
		if (httpOk(response)) {
			setElements(response.data)
			setElementsDuplicate(response.data)
		}
	}

	useEffect(() => {
		getWms()
		getProducts()
		getElements()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{activeTab === 1 &&
				<>
					<div className="page-header d-flex justify-content-between">
						<div className="d-flex text-truncate">
							<h4 className="vertical-center">
								Шаг 1: Приготовленные блюда загатовки
							</h4>
						</div>
						<DropdownButton title={t('action')}>
							<Dropdown.Item onClick={() => fillProducts(1)}>
								Заполнить продуктами
							</Dropdown.Item>
							<Dropdown.Item onClick={() => fillProducts(2)}>
								Заполнить загатовками
							</Dropdown.Item>
							<Dropdown.Item onClick={() => deleteProducts()}>
								Очистить список
							</Dropdown.Item>
						</DropdownButton>
					</div>

					<div className="card mb-3">
						<div className="card-body">
							<div className="row">
								<div className="col-md-2">
									<div className="form-group">
										<label>{t('date')}<span className="required-mark">*</span></label>
										<div className="calendar-input">
											<DatePicker
												showMonthDropdown
												showYearDropdown
												dropdownMode="select"
												yearDropdownItemNumber={2}
												className="form-control"
												dateFormat="dd.MM.yyyy"
												selected={data.created_at ? new Date(data.created_at) : ''}
												onChange={(date) => setData({ ...data, 'created_at': formatDate(date, 'yyyy-MM-dd') })}
												minDate={returnMinDate(new Date(), 20)}
												maxDate={new Date()} />
											<i className="uil uil-calendar-alt"></i>
										</div>
									</div>
								</div>
								<div className="col-md-2">
									<div className="form-groups">
										<label>{t('wms')}<span className="required-mark">*</span></label>
										<Select
											isDisabled={data.elements.length > 0}
											value={wms.find(option => option.id === data.wms_id)}
											options={wms}
											onChange={(option) => handleChangeWMS(option)}
											menuPosition="fixed"
											menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
											menuPlacement="auto"
											styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
								</div>
								<div className="col-md-2">
									<div className="form-groups">
										<label>{t('note')}</label>
										<input name="note" className="form-control"
											autoComplete="off"
											value={data.note ?? ''}
											onChange={(e) => setData({ ...data, 'note': e.target.value })} />
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="card">
						<div className="card-body">
							<div className="doc-table-wrap"
								ref={virtualProducts.containerRef}
								style={{ 'maxHeight': 'calc(100vh - 430px)' }}>
								<table className="table table-bordered doc-table" style={{ 'minWidth': '690px' }}>
									<colgroup>
										<col style={{ 'width': '52px' }} />
										<col />
										<col style={{ 'width': '150px' }} />
										<col style={{ 'width': '130px' }} />
										<col style={{ 'width': '90px' }} />
										<col style={{ 'width': '44px' }} />
									</colgroup>
									<thead>
										<tr>
											<th className="text-center">#</th>
											<th>{t('name2')}</th>
											<th>{t('type')}</th>
											<th className="text-end">{t('counted')}</th>
											<th>{t('unit')}</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{virtualProducts.topSpacer > 0 &&
											<tr className="doc-row-spacer" aria-hidden="true">
												<td colSpan={6} style={{ 'height': `${virtualProducts.topSpacer}px` }}></td>
											</tr>
										}
										{data.products.length === 0 &&
											<tr>
												<td colSpan={6} className="text-center text-muted">
													{data.wms_id ? t('list_empty') : t('inventory_select_wms')}
												</td>
											</tr>
										}
										{data.products.slice(virtualProducts.start, virtualProducts.end).map((item, sliceIndex) => {
											const index = virtualProducts.start + sliceIndex
											return (
										<tr key={index}>
											<td className="text-center">{index + 1}</td>
											<td className="td-ellipsis">
												<ReactSelect
													search={handleSelectSearchProduct}
													value={item.name}
													index={index}
													scrollRef={scrollRef}
													options={products}
													onClose={() => closeReactSelectProduct()}
													selectItem={(option) => handleSelectProduct(option, index)}
													onKeyUp={(e) => {
														if (e.keyCode === 40 || e.keyCode === 38) {
															handleTableKeyDownProduct(e)
														}
													}}
													table={() => (
														<>
															<table className="table mb-0">
																<tbody ref={tbodyRef}>
																	{productOptions.map((selectItem, selectIndex) => (
																		<tr
																			id={selectItem.product_id}
																			className={`cursor ${((selectItem.selected || (selectItem.product_id === item.product_id)) ? 'selected' : '')}`}
																			tabIndex={-1}
																			key={selectIndex}
																			onClick={() => handleSelectProduct(selectItem, index)}
																			onKeyDown={(e) => handleTableKeyDownProduct(e, selectItem, index)}>
																			<td>{selectItem.name}</td>
																			<td className="text-end text-nowrap">{selectItem.product_type_name}</td>
																		</tr>
																	))}
																</tbody>
															</table>
															{products.length > productOptions.length &&
																<div className="doc-options-hint">
																	{t('inventory_options_hint', {
																		shown: productOptions.length,
																		total: products.length,
																	})}
																</div>
															}
														</>
													)}
												/>
											</td>
											<td>{item.product_type_name}</td>
											<td className={`td-input ${!item.actual_balance ? 'is-empty' : ''}`}>
												<input
													type="text"
													name="actual_balance"
													autoComplete="off"
													className={`form-control text-end ${item.actual_balance < 0 ? 'required-border' : ''}`}
													placeholder="0"
													value={item.actual_balance ?? ''}
													onChange={(e) => handleProductChange(e, index)}
													onKeyDown={(e) => handleDecimalKeyDown(e, (event) => handleProductChange(event, index))}
													onKeyUp={(event) => {
														if (event.key === 'Enter') {
															addToList(1, 'product')
														}
													}} />
											</td>
											<td>{item.unit_name}</td>
											<td className="vertical-middle">
												<div className="d-flex justify-content-center"
													onClick={() => deleteProduct(index)}>
													<div className="table-action-button table-action-danger-button">
														<i className="uil uil-times"></i>
													</div>
												</div>
											</td>
										</tr>
											)
										})}
										{virtualProducts.bottomSpacer > 0 &&
											<tr className="doc-row-spacer" aria-hidden="true">
												<td colSpan={6} style={{ 'height': `${virtualProducts.bottomSpacer}px` }}></td>
											</tr>
										}
									</tbody>
								</table>
							</div>

							<div className="doc-footbar">
								<div className="doc-toolbar-actions">
									<input
										type="text"
										className="form-control form-control-sm doc-add-quantity"
										title={t('quantity')}
										tabIndex={-1}
										value={addQuantity}
										onChange={(e) => setAddQuantity(e.target.value.replace(/[^\d]/g, ''))}
									/>
									<button type="button" className="btn btn-sm btn-primary"
										disabled={!data.pos_id}
										onClick={() => {
											setAddQuantity(1)
											addToList(addQuantity, 'product')
										}}>
										<i className="uil uil-plus"></i> {t('add')}
									</button>
								</div>
							</div>

							<div className="btn-wrapper gap-2 mt-5">
								<button type="button" className="btn btn-warning btn-rounded btn-wide"
									onClick={() => navigate('/inventory-wms')}>
									{t('cancel')}
								</button>
								<button type="button" className="btn btn-primary btn-rounded btn-wide"
									disabled={!(data.pos_id || data.wms_id)}
									onClick={() => toggleModalStep(true)}>
									{t('continue')}
								</button>
							</div>
						</div>
					</div>
				</>
			}

			{activeTab === 2 &&
				<>
					<div className="page-header d-flex justify-content-between">
						<div className="d-flex text-truncate">
							<h4 className="vertical-center">
								Шаг 2: Подсчет элементов
							</h4>
						</div>
						<div>
							<button className="btn btn-primary"
								onClick={() => notInventoredList()}>
								{t('not_inventored')}
							</button>
						</div>
						{/* <DropdownButton title={t('action')}>
							<Dropdown.Item onClick={() => fillProducts(1)}>
								Заполнить элементами
							</Dropdown.Item>
						</DropdownButton> */}
					</div>

					<div className="card mb-3">
						<div className="card-body">
							<h4 className="fw-600">{t('inventory')}</h4>
							<div className="d-flex flex-wrap">
								<div className="me-3">
									<div className="d-flex d-none">
										<div className="fw-600 text-nowrap me-3">{t('pos')}:</div>
										<div className="w-100 text-end text-nowrap">{data.pos_name}</div>
									</div>
									<div className="d-flex d-none">
										<div className="fw-600 text-nowrap me-3">{t('wms')}:</div>
										<div className="w-100 text-end text-nowrap">{data.wms_name}</div>
									</div>
									<div className="d-flex">
										<div className="fw-600 text-nowrap me-3">{t('note')}:</div>
										<div className="w-100 text-end text-nowrap">{data.note}</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="card">
						<div className="card-body">
							<div className="doc-table-wrap"
								ref={virtualElements.containerRef}
								style={{ 'maxHeight': 'calc(100vh - 430px)' }}>
								<table className="table table-bordered doc-table" style={{ 'minWidth': '1130px' }}>
									<colgroup>
										<col style={{ 'width': '52px' }} />
										<col />
										<col style={{ 'width': '120px' }} />
										<col style={{ 'width': '80px' }} />
										<col style={{ 'width': '110px' }} />
										<col style={{ 'width': '110px' }} />
										<col style={{ 'width': '110px' }} />
										<col style={{ 'width': '110px' }} />
										<col style={{ 'width': '130px' }} />
										<col style={{ 'width': '44px' }} />
										<col style={{ 'width': '44px' }} />
									</colgroup>
									<thead>
										<tr>
											<th className="text-center">#</th>
											<th>{t('name2')}</th>
											<th className="text-end">{t('counted')}</th>
											<th>{t('unit')}</th>
											<th className="text-end">{t('price')}</th>
											<th className="text-end">{t('fact')}</th>
											<th className="text-end">{t('balance2')}</th>
											<th className="text-end">{t('difference')}</th>
											<th className="text-end">{t('amount')}</th>
											<th></th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										{virtualElements.topSpacer > 0 &&
											<tr className="doc-row-spacer" aria-hidden="true">
												<td colSpan={11} style={{ 'height': `${virtualElements.topSpacer}px` }}></td>
											</tr>
										}
										{data.elements.length === 0 &&
											<tr>
												<td colSpan={11} className="text-center text-muted">{t('list_empty')}</td>
											</tr>
										}
										{data.elements.slice(virtualElements.start, virtualElements.end).map((item, sliceIndex) => {
											const index = virtualElements.start + sliceIndex
											return (
										item.groups?.length > 0 ?
											<Fragment key={index}>
												<tr className="border-none">
													<td className="vertical-middle text-center" rowSpan={item.groups.length}>
														{index + 1}
													</td>
													<td className="vertical-middle border-bottom">
														<div>
															<ReactSelect
																search={handleSelectSearch}
																value={item.name}
																index={index}
																scrollRef={scrollRef}
																options={elements}
																onClose={() => closeReactSelect()}
																selectItem={(option) => handleSelectElement(option, index)}
																onKeyUp={(e) => {
																	if (e.keyCode === 40 || e.keyCode === 38) {
																		handleTableKeyDown(e)
																	}
																}}
																table={() => (
																	<>
																		<table className="table mb-0">
																			<tbody ref={tbodyRef}>
																				{elementOptions.map((selectItem, selectIndex) => (
																					<tr
																						id={selectItem.id}
																						className={`cursor ${((selectItem.selected || (selectItem.id === item.element_id)) ? 'selected' : '')}`}
																						tabIndex={-1}
																						key={selectIndex}
																						onClick={() => handleSelectElement(selectItem, index)}
																						onKeyDown={(e) => handleTableKeyDown(e, selectItem, index)}>
																						<td>{selectItem.name}</td>
																						<td className="text-end text-nowrap">{formatMoney(selectItem.price)}</td>
																					</tr>
																				))}
																			</tbody>
																		</table>
																		{elements.length > elementOptions.length &&
																			<div className="doc-options-hint">
																				{t('inventory_options_hint', {
																					shown: elementOptions.length,
																					total: elements.length,
																				})}
																			</div>
																		}
																	</>
																)}
															/>
														</div>
													</td>
													<td className={`td-input ${!item.groups[0]['actual_balance'] ? 'is-empty' : ''}`}>
														<input
															data-inventory-element-index={index}
															type="text"
															name="actual_balance"
															className={`form-control text-end ${item.groups[0]['actual_balance'] < 0 ? 'required-border' : ''}`}
															placeholder="0"
															value={formatMoneyInput(item.groups[0]['actual_balance']) ?? ''}
															onChange={(e) => handleGroupElementChange(e, index, 0)}
															onKeyDown={(e) => handleDecimalKeyDown(e, (event) => handleGroupElementChange(event, index, 0))}
														/>
													</td>
													<td className="vertical-middle">{item.unit_name}</td>
													<td className="vertical-middle text-end text-nowrap" rowSpan={item.groups.length}>{formatMoney(item.price)}</td>
													<td className="vertical-middle text-end text-nowrap" rowSpan={item.groups.length}>{calculateGroup(item)}</td>
													<td className="vertical-middle text-end text-nowrap" rowSpan={item.groups.length}>{formatMoney(item.balance, 2)}</td>
													<td className="vertical-middle text-end text-nowrap" rowSpan={item.groups.length}>
														<span className={`${item.divergence < 0 ? 'text-danger' : ''}`}>
															{formatMoney(item.divergence, 2)}
														</span>
													</td>
													<td className="vertical-middle text-end text-nowrap" rowSpan={item.groups.length}>
														<span className={`${item.difference_amount < 0 ? 'text-danger' : ''}`}>
															{formatMoney(item.difference_amount)}
														</span>
													</td>
													<td className="vertical-middle" rowSpan={item.groups.length}>
														<div className="d-flex justify-content-end">
															<div className="table-action-button table-action-primary-button"
																onClick={() => addGroupToList(item, index)}>
																<i className="uil uil-plus"></i>
															</div>
														</div>
													</td>
													<td>
														<div className="table-action-button table-action-danger-button"
															onClick={() => deleteElement(index)}>
															<i className="uil uil-times"></i>
														</div>
													</td>
												</tr>
												{item?.groups?.map((item2, index2) => (
													index2 > 0 &&
													<tr className="border-none" key={index2}>
														<td></td>
														<td className={`td-input ${!item2.actual_balance ? 'is-empty' : ''}`}>
															<input
																type="number"
																name="actual_balance"
																className={`form-control text-end ${item2.actual_balance < 0 ? 'required-border' : ''}`}
																placeholder="0"
																value={formatMoneyInput(item2.actual_balance) ?? ''}
																onChange={(e) => handleGroupElementChange(e, index, index2)}
																onKeyDown={(e) => handleDecimalKeyDown(e, (event) => handleGroupElementChange(event, index, index2))}
															/>
														</td>
														<td className="vertical-middle">{item.unit_name}</td>
														<td className="vertical-middle">
															<div className="d-flex justify-content-center">
																<div className="table-action-button table-action-danger-button"
																	onClick={() => deleteGroupProduct(index, index2)}>
																	<i className="uil uil-times"></i>
																</div>
															</div>
														</td>
													</tr>
												))
												}
											</Fragment>
											:
											<tr key={index}>
												<td className="text-center">{index + 1}</td>
												<td>
													<ReactSelect
														isDisabled={item.product_id ? true : false}
														search={handleSelectSearch}
														value={item.name}
														index={index}
														scrollRef={scrollRef}
														options={elements}
														onClose={() => closeReactSelect()}
														selectItem={(option) => handleSelectElement(option, index)}
														onKeyUp={(e) => {
															if (e.keyCode === 40 || e.keyCode === 38) {
																handleTableKeyDown(e)
															}
														}}
														table={() => (
															<>
																<table className="table mb-0">
																	<tbody ref={tbodyRef}>
																		{elementOptions.map((selectItem, selectIndex) => (
																			<tr
																				id={selectItem.element_id}
																				className={`cursor ${((selectItem.selected || (selectItem.element_id === item.element_id)) ? 'selected' : '')}`}
																				tabIndex={-1}
																				key={selectIndex}
																				onClick={() => handleSelectElement(selectItem, index)}
																				onKeyDown={(e) => handleTableKeyDown(e, selectItem, index)}>
																				<td>{selectItem.name}</td>
																				<td className="text-end text-nowrap">{selectItem.product_type_name}</td>
																			</tr>
																		))}
																	</tbody>
																</table>
																{elements.length > elementOptions.length &&
																	<div className="doc-options-hint">
																		{t('inventory_options_hint', {
																			shown: elementOptions.length,
																			total: elements.length,
																		})}
																	</div>
																}
															</>
														)}
													/>
												</td>
												<td className={`td-input ${!item.actual_balance ? 'is-empty' : ''}`}>
													<input
														data-inventory-element-index={index}
														className={`form-control text-end ${item.actual_balance < 0 ? 'required-border' : ''}`}
														type="text"
														name="actual_balance"
														autoComplete="off"
														placeholder="0"
														value={item.actual_balance ?? ''}
														onChange={(e) => handleElementChange(e, index)}
														onKeyDown={(e) => handleDecimalKeyDown(e, (event) => handleElementChange(event, index))}
														onKeyUp={(event) => {
															if (event.key === 'Enter') {
																addToList(1, 'element')
															}
														}}
													/>
												</td>
												<td className="vertical-middle">{item.unit_name}</td>
												<td className="vertical-middle text-end text-nowrap">{formatMoney(item.price)}</td>
												<td className="vertical-middle text-end text-nowrap">{formatMoney(item.actual_balance, 2)}</td>
												<td className="vertical-middle text-end text-nowrap">{formatMoney(item.balance, 2)}</td>
												<td className="text-end text-nowrap">
													<span className={`${item.divergence < 0 ? 'text-danger' : ''}`}>
														{formatMoney(item.divergence, 2)}
													</span>
												</td>
												<td className="text-end text-nowrap">
													<span className={`${item.difference_amount < 0 ? 'text-danger' : ''}`}>
														{formatMoney(item.difference_amount)}
													</span>
												</td>
												<td className="vertical-middle">
													<div className="d-flex justify-content-center gap-2">
														<button className="table-action-button table-action-primary-button"
															onClick={() => addGroupToList(item, index)}>
															<i className="uil uil-plus"></i>
														</button>
													</div>
												</td>
												<td className="vertical-middle">
													<div className="d-flex justify-content-center gap-2">
														<button type="button" className="table-action-button table-action-danger-button"
															onClick={() => deleteElement(index)}>
															<i className="uil uil-times"></i>
														</button>
													</div>
												</td>
											</tr>
											)
										})}
										{virtualElements.bottomSpacer > 0 &&
											<tr className="doc-row-spacer" aria-hidden="true">
												<td colSpan={11} style={{ 'height': `${virtualElements.bottomSpacer}px` }}></td>
											</tr>
										}
									</tbody>
									<tfoot>
										<tr>
											<td></td>
											<td>{t('total')}</td>
											<td></td>
											<td></td>
											<td></td>
											<td className="text-end">{formatMoney(data.difference_quantity, 2)}</td>
											<td></td>
											<td></td>
											<td className="text-end">{formatMoney(data.difference_amount)}</td>
											<td></td>
											<td></td>
										</tr>
									</tfoot>
								</table>
							</div>

							<div className="doc-footbar">
								<div className="doc-toolbar-actions">
									<input
										type="text"
										className="form-control form-control-sm doc-add-quantity"
										title={t('quantity')}
										tabIndex={-1}
										value={addQuantity}
										onChange={(e) => setAddQuantity(e.target.value.replace(/[^\d]/g, ''))}
									/>
									<button type="button" className="btn btn-sm btn-primary"
										disabled={!data.pos_id}
										onClick={() => {
											setAddQuantity(1)
											addToList(addQuantity, 'element')
										}}>
										<i className="uil uil-plus"></i> {t('add')}
									</button>
								</div>
							</div>

							<div className="btn-wrapper gap-2 mt-5">
								<button type="button" className="btn btn-warning btn-rounded btn-wide"
									onClick={() => navigate('/inventory-wms')}>
									{t('cancel')}
								</button>
								<button type="button" className="btn btn-success btn-rounded btn-wide"
									disabled={!data.elements.length > 0}
									onClick={() => requestComplete()}>
									{t('complete')}
								</button>
								<button type="button" className="btn btn-primary btn-rounded btn-wide"
									disabled={!data.elements.length > 0}
									onClick={() => inventoryCompleted()}>
									{t('save_to_draft')}
								</button>
							</div>
						</div>
					</div>
				</>
			}

			<Modal show={modalStep} animation={false}
				centered dialogClassName="update-modal-width"
				onHide={() => toggleModalStep(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('confirm')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					После перехода на следующий шаг вы не сможете изменить кол-во заготовок.
				</Modal.Body>
				<Modal.Footer >
					<div className="d-flex gap-2 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalStep(false)}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => confirmStep()}>
							{t('continue')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>

			{/* MODAL CONFIRM */}
			<Modal show={modalConfirm} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => setModalConfirm(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('confirm')}</Modal.Title>
				</Modal.Header>
				<Modal.Body></Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => setModalConfirm(false)}>
							{t('cancel')}
						</button>
						<button className="btn btn-primary w-100"
							onClick={() => inventoryCompleted(true)}>
							{t('confirm')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>
			{/* MODAL CONFIRM */}

			{/* MODAL DUPLICATES */}
			<Modal show={modalDuplicates} animation={false} centered
				dialogClassName="modal-duplicates-inventory"
				onHide={() => setModalDuplicates(false)}>
				<Modal.Header closeButton>
					<Modal.Title>Дубликаты позиций</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
					<p className="mb-3">
						Эти товары введены несколько раз. При завершении количества будут сложены.
					</p>
					{duplicateSummary.map((item) => (
						<div key={item.element_id} className="mb-3">
							<table className="table table-bordered mb-0">
								<thead>
									<tr>
										<th style={{ width: '50px' }} className="text-center">#</th>
										<th>{t('name2')}</th>
										<th className="text-end" style={{ width: '30%' }}>Кол-во</th>
									</tr>
								</thead>
								<tbody>
									{item.entries.map((entry) => (
										<tr key={`${item.element_id}-${entry.row}`}>
											<td className="text-center">{entry.row}</td>
											<td>{item.name}</td>
											<td className="text-end text-nowrap">{formatMoney(entry.qty, 2)}</td>
										</tr>
									))}
									<tr className="fw-600">
										<td colSpan={2}>Всего</td>
										<td className="text-end text-nowrap">{formatMoney(item.total, 2)}</td>
									</tr>
								</tbody>
							</table>
						</div>
					))}
				</Modal.Body>
				<Modal.Footer>
					<div className="d-flex gap-2 w-100">
						<button type="button" className="btn btn-warning w-100"
							onClick={() => setModalDuplicates(false)}>
							{t('cancel')}
						</button>
						<button type="button" className="btn btn-primary w-100"
							onClick={() => inventoryCompleted(true)}>
							{t('confirm')}
						</button>
					</div>
				</Modal.Footer>
			</Modal>
			{/* MODAL DUPLICATES */}

			<NotInventored
				data={data}
				setData={setData}
				modalNotInventored={modalNotInventored}
				setModalNotInventored={setModalNotInventored}>
			</NotInventored>
		</>
	)
}

export default Main